import React, { useEffect, useMemo, useState } from "react";
import { addDoc, collection, getDocs, serverTimestamp } from "firebase/firestore";
import { db } from "../../lib/firebase";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logo from "../public/assets/logo.png";

function getSignatureUrl(user) {
  return (
    user?.signatureURL ||
    user?.signatureUrl ||
    user?.signatureImageURL ||
    user?.signatureImageUrl ||
    user?.signaturePngURL ||
    user?.signaturePngUrl ||
    ""
  );
}

async function imageUrlToDataUrl(url) {
  if (!url) return "";
  if (url.startsWith("data:image")) return url;

  const response = await fetch(url);
  const blob = await response.blob();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export default function RequisitionBuilderPage({ stockItems = [], styles }) {
  const [search, setSearch] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("all");
  const [sortMode, setSortMode] = useState("none");
  const [selectedItems, setSelectedItems] = useState({});
  const [requisitionPreviewOpen, setRequisitionPreviewOpen] = useState(false);

  const suppliers = useMemo(() => {
    return Array.from(
      new Set(stockItems.map((item) => item.supplier).filter(Boolean))
    ).sort();
  }, [stockItems]);

  const consolidatedItems = useMemo(() => {
    const map = {};

    stockItems.forEach((item) => {
      const key = `${item.itemName || ""}_${item.supplier || ""}_${
        item.itemNumber || item.vendorPartNumber || ""
      }`;

      if (!map[key]) {
        map[key] = {
          ...item,
          totalQuantityOnHand: 0,
          expirationBreakdown: {},
        };
      }

      const qty = Number(item.quantityOnHand || 0);
      const lots = Array.isArray(item.lots) && item.lots.length > 0
        ? item.lots
        : [{ expirationDate: item.expirationDate || "No Expiration Listed", quantity: qty }];

      map[key].totalQuantityOnHand += qty;

      lots.forEach((lot) => {
        const exp = lot.expirationDate || "No Expiration Listed";
        map[key].expirationBreakdown[exp] =
          (map[key].expirationBreakdown[exp] || 0) + Number(lot.quantity || 0);
      });
    });

    return Object.values(map);
  }, [stockItems]);

  const filteredItems = useMemo(() => {
    const q = search.toLowerCase();

    return consolidatedItems
      .filter((item) => {
        const matchesSearch =
          item.itemName?.toLowerCase().includes(q) ||
          item.category?.toLowerCase().includes(q) ||
          item.supplier?.toLowerCase().includes(q) ||
          item.itemNumber?.toLowerCase().includes(q) ||
          item.vendorPartNumber?.toLowerCase().includes(q) ||
          item.peoplesoftNumber?.toLowerCase().includes(q);

        const matchesSupplier =
          supplierFilter === "all" || item.supplier === supplierFilter;

        return matchesSearch && matchesSupplier;
      })
      .sort((a, b) => {
        if (sortMode === "category") {
          const categoryCompare = (a.category || "").localeCompare(b.category || "");
          if (categoryCompare !== 0) return categoryCompare;
          return (a.itemName || "").localeCompare(b.itemName || "");
        }

        if (sortMode === "az") {
          return (a.itemName || "").localeCompare(b.itemName || "");
        }

        if (sortMode === "za") {
          return (b.itemName || "").localeCompare(a.itemName || "");
        }

        if (sortMode === "supplier") {
          const supplierCompare = (a.supplier || "").localeCompare(b.supplier || "");
          if (supplierCompare !== 0) return supplierCompare;
          return (a.itemName || "").localeCompare(b.itemName || "");
        }

        return 0;
      });
  }, [consolidatedItems, search, supplierFilter, sortMode]);

  function toggleSelectedItem(item) {
    setSelectedItems((prev) => {
      const existing = prev[item.id];

      if (existing) {
        const updated = { ...prev };
        delete updated[item.id];
        return updated;
      }

      return {
        ...prev,
        [item.id]: {
          item,
          orderQty: 1,
        },
      };
    });
  }

  function updateSelectedOrderQty(itemId, value) {
    setSelectedItems((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        orderQty: Math.max(0, Number(value || 0)),
      },
    }));
  }

  const selectedList = Object.values(selectedItems);

  const requisitionTotal = selectedList.reduce((sum, entry) => {
    return sum + Number(entry.item.price || 0) * Number(entry.orderQty || 0);
  }, 0);

  return (
    <div style={{ ...styles.panel, width: "100%", boxSizing: "border-box" }}>
      <div style={styles.panelHeader}>
        <div>
          <h2 style={styles.panelTitle}>Requisition Builder</h2>
          <p style={styles.subtitle}>
            Build purchase requisitions from current inventory items.
          </p>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "minmax(220px, 1fr) minmax(160px, 220px) minmax(160px, 220px)",
          gap: 10,
          margin: "12px 0 14px",
          alignItems: "end",
        }}
      >
        <div>
          <label style={styles.fieldLabel}>Search</label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search item, category, supplier, or part number..."
            style={styles.searchInput}
          />
        </div>

        <div>
          <label style={styles.fieldLabel}>Supplier</label>
          <select
            value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">All Suppliers</option>
            {suppliers.map((supplier) => (
              <option key={supplier} value={supplier}>
                {supplier}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={styles.fieldLabel}>Sort By</label>
          <select
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="none">Default Sort</option>
            <option value="az">Item A–Z</option>
            <option value="za">Item Z–A</option>
            <option value="category">Category then A–Z</option>
            <option value="supplier">Supplier then A–Z</option>
          </select>
        </div>
      </div>

      <div style={styles.requisitionFooter}>
        <div>
          <strong>{selectedList.length}</strong> item(s) selected
          <div style={{ color: "#64748b", fontSize: 13 }}>
            Estimated total: ${requisitionTotal.toFixed(2)}
          </div>
        </div>

        <button
          disabled={selectedList.length === 0}
          onClick={() => setRequisitionPreviewOpen(true)}
          style={{
            ...styles.primaryButton,
            opacity: selectedList.length === 0 ? 0.5 : 1,
          }}
        >
          View / Create Requisition
        </button>
      </div>

      {requisitionPreviewOpen && (
        <RequisitionPreviewModal
          styles={styles}
          selectedList={selectedList}
          requisitionTotal={requisitionTotal}
          onClose={() => setRequisitionPreviewOpen(false)}
          setRequisitionPreviewOpen={setRequisitionPreviewOpen}
        />
      )}

      <div style={{ overflowX: "auto", width: "100%", maxWidth: "100%" }}>
        <table style={{ ...styles.table, minWidth: 1320 }}>
          <thead>
            <tr>
              <th style={styles.th}>Select</th>
              <th style={styles.th}>Item</th>
              <th style={styles.th}>Supplier</th>
              <th style={styles.th}>Peoplesoft #</th>
              <th style={styles.th}>Vendor Part #</th>
              <th style={styles.th}>Mfg #</th>
              <th style={styles.th}>Count Unit</th>
              <th style={styles.th}>Qty On Hand</th>
              <th style={styles.th}>Order Pack</th>
              <th style={styles.th}>Units/Pack</th>
              <th style={styles.th}>Price</th>
              <th style={styles.th}>PAR / Reorder</th>
              <th style={styles.th}>Expiration</th>
              <th style={styles.th}>Order Qty</th>
            </tr>
          </thead>

          <tbody>
            {filteredItems.map((item) => {
              const selected = selectedItems[item.id];

              return (
                <tr
                  key={item.id}
                  style={{ background: selected ? "#ecfdf5" : "#fff" }}
                >
                  <td style={styles.td}>
                    <input
                      type="checkbox"
                      checked={!!selected}
                      onChange={() => toggleSelectedItem(item)}
                    />
                  </td>

                  <td style={styles.td}>
                    <strong>{item.itemName}</strong>
                    <div style={{ fontSize: 11, color: "#64748b" }}>
                      {item.category || "Uncategorized"}
                    </div>
                  </td>

                  <td style={styles.td}>{item.supplier || item.vendor || "—"}</td>
                  <td style={styles.td}>{item.peoplesoftNumber || "—"}</td>
                  <td style={styles.td}>
                    {item.vendorPartNumber || item.itemNumber || "—"}
                  </td>
                  <td style={styles.td}>
                    {item.manufacturerNumber || item.psMfgNumber || "—"}
                  </td>
                  <td style={styles.td}>{item.unitType || "—"}</td>

                  <td style={styles.td}>
                    <strong>
                      {item.totalQuantityOnHand ?? 0} {item.unitType || ""}
                    </strong>
                  </td>

                  <td style={styles.td}>
                    {item.orderPackType || item.unitType || "—"}
                  </td>

                  <td style={styles.td}>{item.unitsPerPack || item.unitCount || 1}</td>

                  <td style={styles.td}>
                    {item.price != null ? `$${Number(item.price).toFixed(2)}` : "—"}
                  </td>

                  <td style={styles.td}>
                    {item.parLevel ?? 0} / {item.reorderLevel ?? 0}
                  </td>

                  <td style={styles.td}>
                    {Object.entries(item.expirationBreakdown || {}).map(
                      ([date, qty]) => (
                        <div key={date}>
                          <strong>{qty}</strong> — {date}
                        </div>
                      )
                    )}
                  </td>

                  <td style={styles.td}>
                    <input
                      type="number"
                      min="0"
                      disabled={!selected}
                      value={selected?.orderQty || ""}
                      onChange={(e) =>
                        updateSelectedOrderQty(item.id, e.target.value)
                      }
                      style={{
                        ...styles.qtyInput,
                        opacity: selected ? 1 : 0.45,
                      }}
                    />
                  </td>
                </tr>
              );
            })}

            {filteredItems.length === 0 && (
              <tr>
                <td style={styles.td} colSpan="14">
                  No matching items found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RequisitionPreviewModal({
  styles,
  selectedList,
  requisitionTotal,
  onClose,
  setRequisitionPreviewOpen,
}) {
  const [superUsers, setSuperUsers] = useState([]);
  const [requestedById, setRequestedById] = useState("");
  const [approvedById, setApprovedById] = useState("");

  const selectedRequester = superUsers.find((u) => u.id === requestedById);
  const selectedApprover = superUsers.find((u) => u.id === approvedById);

  useEffect(() => {
    async function loadSuperUsers() {
      try {
        const snap = await getDocs(collection(db, "users"));

        const users = snap.docs
          .map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          }))
          .filter((user) => {
            const role = String(user.role || "").toLowerCase();
            return (
              role === "super user" ||
              role === "super_admin" ||
              role === "super admin" ||
              role === "super_user"
            );
          });

        setSuperUsers(users);

        if (users.length > 0) {
          setRequestedById(users[0].id);
          setApprovedById(users[0].id);
        }
      } catch (err) {
        console.error("Failed to load super users:", err);
      }
    }

    loadSuperUsers();
  }, []);

  const groupedBySupplier = selectedList.reduce((acc, entry) => {
    const supplier = entry.item.supplier || entry.item.vendor || "No Supplier Listed";
    if (!acc[supplier]) acc[supplier] = [];
    acc[supplier].push(entry);
    return acc;
  }, {});

  async function handleCreateRequisition() {
    try {
      const requisitionItems = selectedList.map((entry) => {
        const item = entry.item;

        return {
          itemId: item.id,
          itemName: item.itemName || "",
          supplier: item.supplier || item.vendor || "",
          peoplesoftNumber: item.peoplesoftNumber || "",
          vendorPartNumber: item.vendorPartNumber || item.itemNumber || "",
          manufacturerNumber: item.manufacturerNumber || item.psMfgNumber || "",
          unitType: item.unitType || "",
          price: Number(item.price || 0),
          qtyOnHand: Number(item.totalQuantityOnHand || 0),
          parLevel: Number(item.parLevel || 0),
          reorderLevel: Number(item.reorderLevel || 0),
          orderQty: Number(entry.orderQty || 0),
          lineTotal: Number(item.price || 0) * Number(entry.orderQty || 0),
        };
      });

      await addDoc(collection(db, "inventoryRequisitions"), {
        status: "created",
        createdAt: serverTimestamp(),
        estimatedTotal: requisitionTotal,
        requestedBy: {
          id: selectedRequester?.id || "",
          name:
            selectedRequester?.displayName ||
            selectedRequester?.name ||
            selectedRequester?.email ||
            "",
          email: selectedRequester?.email || "",
          signatureUrl: getSignatureUrl(selectedRequester),
        },
        approvedBy: {
          id: selectedApprover?.id || "",
          name:
            selectedApprover?.displayName ||
            selectedApprover?.name ||
            selectedApprover?.email ||
            "",
          email: selectedApprover?.email || "",
          signatureUrl: getSignatureUrl(selectedApprover),
        },
        items: requisitionItems,
      });

      const docPdf = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "letter",
      });

      const requisitionNumber = `REQ-${Date.now()}`;

      docPdf.addImage(logo, "PNG", 10, 8, 28, 28);
      docPdf.setFontSize(20);
      docPdf.setTextColor(25, 45, 80);
      docPdf.text("Travelers Championship Medical Team", 45, 18);

      docPdf.setFontSize(16);
      docPdf.text("Supply Requisition", 45, 27);

      docPdf.setFontSize(10);
      docPdf.setTextColor(80);
      docPdf.text(`Generated: ${new Date().toLocaleString()}`, 45, 35);
      docPdf.text(`Requisition #: ${requisitionNumber}`, 45, 41);
      docPdf.text(
        `Requested By: ${
          selectedRequester?.displayName ||
          selectedRequester?.name ||
          selectedRequester?.email ||
          "Not selected"
        }`,
        45,
        47
      );
      docPdf.text(
        `Approved By: ${
          selectedApprover?.displayName ||
          selectedApprover?.name ||
          selectedApprover?.email ||
          "Not selected"
        }`,
        45,
        53
      );

      const tableRows = requisitionItems.map((item) => [
        item.itemName || "",
        item.supplier || "",
        item.peoplesoftNumber || "",
        item.vendorPartNumber || "",
        item.manufacturerNumber || "",
        item.unitType || "",
        item.orderQty || "",
      ]);

      autoTable(docPdf, {
        startY: 64,
        head: [[
          "Item",
          "Vendor",
          "Peoplesoft #",
          "Vendor Part #",
          "Mfg #",
          "Unit",
          "Qty",
        ]],
        body: tableRows,
        styles: {
          fontSize: 9,
          cellPadding: 3,
          overflow: "linebreak",
          valign: "middle",
        },
        headStyles: {
          fillColor: [25, 45, 80],
          textColor: 255,
          fontStyle: "bold",
        },
        alternateRowStyles: {
          fillColor: [245, 247, 250],
        },
        margin: {
          left: 10,
          right: 10,
        },
      });

      const finalY = docPdf.lastAutoTable?.finalY || 70;

      let requesterSignature = "";
      let approverSignature = "";

      try {
        requesterSignature = await imageUrlToDataUrl(
          getSignatureUrl(selectedRequester)
        );
      } catch (err) {
        console.warn("Requester signature could not be loaded:", err);
      }

      try {
        approverSignature = await imageUrlToDataUrl(
          getSignatureUrl(selectedApprover)
        );
      } catch (err) {
        console.warn("Approver signature could not be loaded:", err);
      }

      docPdf.setDrawColor(180);
      docPdf.setFontSize(10);

      if (requesterSignature) {
        try {
          docPdf.addImage(requesterSignature, "PNG", 12, finalY + 2, 50, 18);
        } catch (err) {
          console.error("Failed requester signature:", err);
        }
      }

      docPdf.line(10, finalY + 24, 120, finalY + 24);
      docPdf.text(
        `Requested By: ${
          selectedRequester?.displayName ||
          selectedRequester?.name ||
          selectedRequester?.email ||
          ""
        }`,
        10,
        finalY + 30
      );

      if (approverSignature) {
        try {
          docPdf.addImage(approverSignature, "PNG", 152, finalY + 2, 50, 18);
        } catch (err) {
          console.error("Failed approver signature:", err);
        }
      }

      docPdf.line(150, finalY + 24, 260, finalY + 24);
      docPdf.text(
        `Approved By: ${
          selectedApprover?.displayName ||
          selectedApprover?.name ||
          selectedApprover?.email ||
          ""
        }`,
        150,
        finalY + 30
      );

      docPdf.save(`Requisition_${new Date().toISOString().slice(0, 10)}.pdf`);
      setRequisitionPreviewOpen(false);
    } catch (err) {
      console.error("Failed to create requisition:", err);
      alert("Failed to create requisition.");
    }
  }

  return (
    <div style={styles.modalOverlay}>
      <div style={{ ...styles.modal, maxWidth: 1000 }}>
        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>Requisition Preview</h2>
            <p style={styles.subtitle}>
              Review selected items before generating a requisition.
            </p>

            <div style={{ marginTop: 12 }}>
              <label style={styles.fieldLabel}>Requested By</label>
              <select
                value={requestedById}
                onChange={(e) => setRequestedById(e.target.value)}
                style={styles.select}
              >
                {superUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.displayName || user.name || user.email}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={styles.fieldLabel}>Approved By</label>
              <select
                value={approvedById}
                onChange={(e) => setApprovedById(e.target.value)}
                style={styles.select}
              >
                {superUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.displayName || user.name || user.email}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <button onClick={onClose} style={styles.closeButton}>
            Close
          </button>
        </div>

        <div style={{ marginTop: 16 }}>
          {Object.entries(groupedBySupplier).map(([supplier, entries]) => (
            <div key={supplier} style={styles.supplierGroup}>
              <h3 style={{ marginTop: 0 }}>{supplier}</h3>

              <div style={{ overflowX: "auto" }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Item</th>
                      <th style={styles.th}>Peoplesoft #</th>
                      <th style={styles.th}>Vendor Part #</th>
                      <th style={styles.th}>Mfg #</th>
                      <th style={styles.th}>Unit</th>
                      <th style={styles.th}>Order Qty</th>
                      <th style={styles.th}>Price</th>
                      <th style={styles.th}>Line Total</th>
                    </tr>
                  </thead>

                  <tbody>
                    {entries.map((entry) => {
                      const item = entry.item;
                      const qty = Number(entry.orderQty || 0);
                      const price = Number(item.price || 0);
                      const lineTotal = qty * price;

                      return (
                        <tr key={item.id}>
                          <td style={styles.td}>{item.itemName}</td>
                          <td style={styles.td}>{item.peoplesoftNumber || "—"}</td>
                          <td style={styles.td}>
                            {item.vendorPartNumber || item.itemNumber || "—"}
                          </td>
                          <td style={styles.td}>
                            {item.manufacturerNumber || item.psMfgNumber || "—"}
                          </td>
                          <td style={styles.td}>{item.unitType || "—"}</td>
                          <td style={styles.td}>{qty}</td>
                          <td style={styles.td}>${price.toFixed(2)}</td>
                          <td style={styles.td}>${lineTotal.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>

        <div style={styles.cartTotal}>
          <span>Estimated Total</span>
          <strong>${requisitionTotal.toFixed(2)}</strong>
        </div>

        <div style={styles.modalActions}>
          <button style={styles.primaryButton} onClick={handleCreateRequisition}>
            Create Requisition PDF
          </button>
        </div>
      </div>
    </div>
  );
}